import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Square, Diamond, PlayCircle, StopCircle, StickyNote, ChevronDown, Search } from 'lucide-react';
import { BlockType } from '../../lib/types';
import { BPMN_CATEGORIES, BpmnSymbolDefinition } from '../../data/bpmnSymbols';
import {
  DMN_ROW1_SYMBOLS,
  getDmnPaletteSymbols,
  DmnSymbolDefinition,
} from '../../data/dmnSymbols';
import {
  VSM_CATEGORIES,
  VsmCategoryId,
  VsmSymbolDefinition,
} from '../../data/vsmSymbols';
import { BpmnSymbolIcon } from './BpmnSymbolIcon';
import { DmnSymbolIcon } from './DmnSymbolIcon';
import { VsmSymbolIcon } from './VsmSymbolIcon';

export const SHAPE_DRAG_TYPE_KEY = 'shape-type';
export const BPMN_DRAG_SYMBOL_KEY = 'bpmn-symbol';
export const DMN_DRAG_SYMBOL_KEY = 'dmn-symbol';
export const VSM_DRAG_SYMBOL_KEY = 'vsm-symbol';

export interface EpcShapeDefinition {
  type: BlockType;
  label: string;
  color: 'blue' | 'purple' | 'green' | 'red' | 'yellow';
  Icon: React.ComponentType<{ className?: string }>;
}

const DEFAULT_EPC_SHAPES: EpcShapeDefinition[] = [
  { type: 'process', label: 'Process', color: 'blue', Icon: Square },
  { type: 'decision', label: 'Decision', color: 'purple', Icon: Diamond },
  { type: 'start', label: 'Start', color: 'green', Icon: PlayCircle },
  { type: 'end', label: 'End', color: 'red', Icon: StopCircle },
  { type: 'note', label: 'Note', color: 'yellow', Icon: StickyNote },
];

const colorClasses: Record<string, string> = {
  blue: 'bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-100',
  purple: 'bg-purple-50 text-purple-600 hover:bg-purple-100 border-purple-100',
  green: 'bg-green-50 text-green-600 hover:bg-green-100 border-green-100',
  red: 'bg-red-50 text-red-600 hover:bg-red-100 border-red-100',
  yellow: 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border-yellow-100',
  neutral: 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-200',
  orange: 'bg-orange-50 text-orange-700 hover:bg-orange-100 border-orange-100',
};

interface SymbolPaletteProps {
  /** library = draw.io-style left panel; sidebar/toolbar = compact variants */
  variant?: 'library' | 'sidebar' | 'toolbar';
  epcShapes?: EpcShapeDefinition[];
  onDragStart?: (payload: {
    blockType: BlockType;
    bpmnSymbol?: string;
    dmnSymbol?: string;
    vsmSymbol?: string;
  }) => void;
  className?: string;
}

function setDragPayload(
  e: React.DragEvent,
  blockType: BlockType,
  opts?: { bpmnSymbol?: string; dmnSymbol?: string; vsmSymbol?: string }
) {
  e.dataTransfer.setData(SHAPE_DRAG_TYPE_KEY, blockType);
  if (opts?.bpmnSymbol) e.dataTransfer.setData(BPMN_DRAG_SYMBOL_KEY, opts.bpmnSymbol);
  if (opts?.dmnSymbol) e.dataTransfer.setData(DMN_DRAG_SYMBOL_KEY, opts.dmnSymbol);
  if (opts?.vsmSymbol) e.dataTransfer.setData(VSM_DRAG_SYMBOL_KEY, opts.vsmSymbol);
  e.dataTransfer.effectAllowed = 'copy';
}

function SectionHeader({
  label,
  count,
  open,
  onToggle,
}: {
  label: string;
  count: number;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between px-2 py-1.5 text-left hover:bg-gray-100/80 transition-colors"
    >
      <span className="text-xs font-semibold text-gray-700 tracking-wide">{label}</span>
      <span className="flex items-center gap-1 text-[10px] text-gray-400">
        {count}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </span>
    </button>
  );
}

function SymbolChip({
  label,
  colorToken,
  title,
  onDragStart,
  children,
}: {
  label: string;
  colorToken: string;
  title?: string;
  onDragStart?: (e: React.DragEvent) => void;
  children: React.ReactNode;
}) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      className={`flex flex-col items-center p-2 rounded-md border transition-colors cursor-grab active:cursor-grabbing ${
        colorClasses[colorToken] ?? colorClasses.neutral
      }`}
      title={title ?? label}
    >
      <div className="w-full aspect-[5/4] flex items-center justify-center overflow-hidden">
        {children}
      </div>
      <span className="text-[10px] font-medium text-center mt-1 leading-tight line-clamp-2 w-full">{label}</span>
    </div>
  );
}

export const SymbolPalette: React.FC<SymbolPaletteProps> = ({
  variant = 'library',
  epcShapes = DEFAULT_EPC_SHAPES,
  onDragStart,
  className = '',
}) => {
  const { t } = useTranslation();
  const bpmnSymbolLabel = useCallback(
    (symbol: BpmnSymbolDefinition) =>
      t(`features.swimlane.bpmn.${symbol.id}`, { defaultValue: symbol.label }),
    [t]
  );
  const dmnSymbolLabel = useCallback(
    (symbol: DmnSymbolDefinition) =>
      t(`features.swimlane.dmn.${symbol.id}`, { defaultValue: symbol.label }),
    [t]
  );
  const vsmSymbolLabel = useCallback(
    (symbol: VsmSymbolDefinition) =>
      t(`features.swimlane.vsm.${symbol.id}`, { defaultValue: symbol.label }),
    [t]
  );

  const [query, setQuery] = useState('');
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    general: true,
    events: true,
    activities: false,
    gateways: false,
    artifacts: false,
    dmn: true,
    vsm: true,
  });
  const [dmnShowMore, setDmnShowMore] = useState(false);
  const [vsmTab, setVsmTab] = useState<VsmCategoryId>('process');
  const [vsmShowMore, setVsmShowMore] = useState<Record<VsmCategoryId, boolean>>({
    process: false,
    material: false,
    information: false,
    general: false,
  });

  const handleEpcDragStart = useCallback(
    (e: React.DragEvent, type: BlockType) => {
      setDragPayload(e, type);
      onDragStart?.({ blockType: type });
    },
    [onDragStart]
  );

  const handleBpmnDragStart = useCallback(
    (e: React.DragEvent, symbol: BpmnSymbolDefinition) => {
      setDragPayload(e, symbol.blockType, { bpmnSymbol: symbol.id });
      onDragStart?.({ blockType: symbol.blockType, bpmnSymbol: symbol.id });
    },
    [onDragStart]
  );

  const handleDmnDragStart = useCallback(
    (e: React.DragEvent, symbol: DmnSymbolDefinition) => {
      setDragPayload(e, symbol.blockType, { dmnSymbol: symbol.id });
      onDragStart?.({ blockType: symbol.blockType, dmnSymbol: symbol.id });
    },
    [onDragStart]
  );

  const handleVsmDragStart = useCallback(
    (e: React.DragEvent, symbol: VsmSymbolDefinition) => {
      setDragPayload(e, symbol.blockType, { vsmSymbol: symbol.id });
      onDragStart?.({ blockType: symbol.blockType, vsmSymbol: symbol.id });
    },
    [onDragStart]
  );

  const toggleSection = (id: string) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const normalizedQuery = query.trim().toLowerCase();

  const filteredEpc = useMemo(() => {
    if (!normalizedQuery) return epcShapes;
    return epcShapes.filter(
      (s) => s.label.toLowerCase().includes(normalizedQuery) || s.type.includes(normalizedQuery)
    );
  }, [epcShapes, normalizedQuery]);

  const filteredCategories = useMemo(() => {
    if (!normalizedQuery) return BPMN_CATEGORIES;
    return BPMN_CATEGORIES.map((cat) => ({
      ...cat,
      symbols: cat.symbols.filter(
        (s) =>
          s.label.toLowerCase().includes(normalizedQuery) ||
          s.xmlType.toLowerCase().includes(normalizedQuery) ||
          s.id.toLowerCase().includes(normalizedQuery)
      ),
    })).filter((cat) => cat.symbols.length > 0);
  }, [normalizedQuery]);

  const filteredDmn = useMemo(() => {
    const all = getDmnPaletteSymbols();
    if (!normalizedQuery) return all;
    return all.filter(
      (s) =>
        s.label.toLowerCase().includes(normalizedQuery) ||
        s.xmlType.toLowerCase().includes(normalizedQuery) ||
        s.id.toLowerCase().includes(normalizedQuery)
    );
  }, [normalizedQuery]);

  const dmnVisible = useMemo(() => {
    if (normalizedQuery) return filteredDmn;
    if (dmnShowMore) return filteredDmn;
    return filteredDmn.filter((s) => DMN_ROW1_SYMBOLS.includes(s.id));
  }, [filteredDmn, dmnShowMore, normalizedQuery]);

  const filteredVsmCategories = useMemo(() => {
    if (!normalizedQuery) return VSM_CATEGORIES;
    return VSM_CATEGORIES.map((cat) => ({
      ...cat,
      symbols: cat.symbols.filter(
        (s) =>
          s.label.toLowerCase().includes(normalizedQuery) ||
          s.id.toLowerCase().includes(normalizedQuery) ||
          s.kind.includes(normalizedQuery)
      ),
    })).filter((cat) => cat.symbols.length > 0);
  }, [normalizedQuery]);

  const activeVsmCategory = useMemo(() => {
    const fromFilter = filteredVsmCategories.find((c) => c.id === vsmTab);
    return fromFilter ?? filteredVsmCategories[0] ?? VSM_CATEGORIES[0];
  }, [filteredVsmCategories, vsmTab]);

  const vsmVisible = useMemo(() => {
    if (!activeVsmCategory) return [];
    if (normalizedQuery || vsmShowMore[activeVsmCategory.id]) {
      return activeVsmCategory.symbols;
    }
    const row1 = new Set(activeVsmCategory.row1);
    return activeVsmCategory.symbols.filter((s) => row1.has(s.id));
  }, [activeVsmCategory, normalizedQuery, vsmShowMore]);

  // Compact toolbar: only EPC chips (BPMN/DMN/VSM live in the library panel)
  if (variant === 'toolbar') {
    return (
      <div className={`flex items-center gap-1 flex-nowrap ${className}`}>
        {epcShapes.map(({ type, label, color, Icon }) => (
          <button
            key={type}
            type="button"
            draggable
            onDragStart={(e) => handleEpcDragStart(e, type)}
            className={`shrink-0 flex flex-col items-center justify-center p-2 rounded-lg transition-colors border ${colorClasses[color]}`}
            title={label}
          >
            <Icon className="w-5 h-5" />
            <span className="text-[10px] capitalize mt-0.5 leading-tight">{label}</span>
          </button>
        ))}
      </div>
    );
  }

  const noMatches =
    normalizedQuery &&
    filteredEpc.length === 0 &&
    filteredCategories.length === 0 &&
    filteredDmn.length === 0 &&
    filteredVsmCategories.length === 0;

  return (
    <div className={`flex flex-col h-full min-h-0 ${className}`}>
      <div className="px-2 pt-2 pb-1 shrink-0">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter shapes…"
            className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded-md bg-white text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-1 pb-3 space-y-1">
        {/* General / EPC */}
        {(filteredEpc.length > 0 || !normalizedQuery) && (
          <div className="rounded-md border border-gray-200 bg-white overflow-hidden">
            <SectionHeader
              label="General"
              count={filteredEpc.length}
              open={openSections.general || Boolean(normalizedQuery)}
              onToggle={() => toggleSection('general')}
            />
            {(openSections.general || Boolean(normalizedQuery)) && (
              <div className="grid grid-cols-2 gap-1.5 p-2 pt-0">
                {filteredEpc.map(({ type, label, color, Icon }) => (
                  <div
                    key={type}
                    draggable
                    onDragStart={(e) => handleEpcDragStart(e, type)}
                    className={`flex flex-col items-center justify-center p-2.5 rounded-md cursor-grab active:cursor-grabbing border transition-colors ${colorClasses[color]}`}
                    title={label}
                  >
                    <Icon className="w-5 h-5 mb-1" />
                    <span className="text-[11px] font-medium text-center leading-tight">{label}</span>
                  </div>
                ))}
                {filteredEpc.length === 0 && (
                  <p className="col-span-2 text-[11px] text-gray-400 text-center py-2">No matches</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* BPMN categories */}
        {filteredCategories.map((category) => {
          const isOpen = openSections[category.id] || Boolean(normalizedQuery);
          return (
            <div key={category.id} className="rounded-md border border-gray-200 bg-white overflow-hidden">
              <SectionHeader
                label={category.label}
                count={category.symbols.length}
                open={isOpen}
                onToggle={() => toggleSection(category.id)}
              />
              {isOpen && (
                <div className="grid grid-cols-2 gap-1.5 p-2 pt-0">
                  {category.symbols.map((symbol) => (
                    <SymbolChip
                      key={symbol.id}
                      label={bpmnSymbolLabel(symbol)}
                      colorToken={symbol.colorToken}
                      onDragStart={(e) => handleBpmnDragStart(e, symbol)}
                    >
                      <BpmnSymbolIcon symbolId={symbol.id} size={32} />
                    </SymbolChip>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* DMN — progressive disclosure (PRD §7.2) */}
        {(filteredDmn.length > 0 || !normalizedQuery) && (
          <div className="rounded-md border border-gray-200 bg-white overflow-hidden">
            <SectionHeader
              label="DMN"
              count={filteredDmn.length}
              open={openSections.dmn || Boolean(normalizedQuery)}
              onToggle={() => toggleSection('dmn')}
            />
            {(openSections.dmn || Boolean(normalizedQuery)) && (
              <div className="p-2 pt-0 space-y-2">
                <div className="grid grid-cols-2 gap-1.5">
                  {dmnVisible.map((symbol) => (
                    <SymbolChip
                      key={symbol.id}
                      label={dmnSymbolLabel(symbol)}
                      colorToken={symbol.colorToken}
                      title={`${dmnSymbolLabel(symbol)} (${symbol.xmlType})`}
                      onDragStart={(e) => handleDmnDragStart(e, symbol)}
                    >
                      <DmnSymbolIcon symbolId={symbol.id} size={36} />
                    </SymbolChip>
                  ))}
                </div>
                {!normalizedQuery && (
                  <button
                    type="button"
                    onClick={() => setDmnShowMore((v) => !v)}
                    className="w-full text-[11px] font-medium text-gray-600 hover:text-gray-900 py-1 rounded hover:bg-gray-50"
                  >
                    {dmnShowMore ? 'Show less' : 'More DMN symbols'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* VSM — category tabs (PRD §7.2) */}
        {(filteredVsmCategories.length > 0 || !normalizedQuery) && (
          <div className="rounded-md border border-gray-200 bg-white overflow-hidden">
            <SectionHeader
              label="VSM"
              count={VSM_CATEGORIES.reduce((n, c) => n + c.symbols.length, 0)}
              open={openSections.vsm || Boolean(normalizedQuery)}
              onToggle={() => toggleSection('vsm')}
            />
            {(openSections.vsm || Boolean(normalizedQuery)) && (
              <div className="p-2 pt-0 space-y-2">
                <div className="flex flex-wrap gap-1">
                  {filteredVsmCategories.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setVsmTab(cat.id)}
                      className={`px-2 py-0.5 rounded text-[10px] font-semibold border transition-colors ${
                        activeVsmCategory?.id === cat.id
                          ? 'bg-gray-800 text-white border-gray-800'
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {cat.label}
                      <span className="ml-1 opacity-70">{cat.symbols.length}</span>
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {vsmVisible.map((symbol) => (
                    <SymbolChip
                      key={symbol.id}
                      label={vsmSymbolLabel(symbol)}
                      colorToken={symbol.colorToken}
                      title={`${vsmSymbolLabel(symbol)} · ${symbol.kind}`}
                      onDragStart={(e) => handleVsmDragStart(e, symbol)}
                    >
                      <VsmSymbolIcon symbolId={symbol.id} size={36} />
                    </SymbolChip>
                  ))}
                </div>
                {!normalizedQuery && activeVsmCategory && (
                  <button
                    type="button"
                    onClick={() =>
                      setVsmShowMore((prev) => ({
                        ...prev,
                        [activeVsmCategory.id]: !prev[activeVsmCategory.id],
                      }))
                    }
                    className="w-full text-[11px] font-medium text-gray-600 hover:text-gray-900 py-1 rounded hover:bg-gray-50"
                  >
                    {vsmShowMore[activeVsmCategory.id]
                      ? 'Show less'
                      : `More ${activeVsmCategory.label}`}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {noMatches && (
          <p className="text-xs text-gray-400 text-center py-6">No shapes match “{query}”</p>
        )}
      </div>
    </div>
  );
};

export default SymbolPalette;
